import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Grid,
  TextField,
  Typography,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import RichTextEditor from "./RichTextEditor";
import FileUploadButton from "./FileUploadButton";
import FillBlankInput from "./FillBlankInput";
import { useRichTextWithUpload } from "@/hooks";

/**
 * QuestionForm - Self-contained form component for creating/editing questions
 *
 * @param {Object} initialData - Initial question data
 * @param {Function} onChange - Callback when form data changes (receives full formData object)
 * @param {Object} previousQuestion - Previous question data (for media reuse)
 * @param {boolean} showContent - Whether to show content editor (for reading section)
 * @param {boolean} isEditing - Whether in edit mode
 * @param {boolean} allowMultipleTypes - Whether to allow switching between multiple-choice and fill-blank types (default: false)
 * @param {React.Ref} ref - Ref to expose processImagesAndGetData method
 */
const QuestionForm = forwardRef(
  (
    {
      initialData = {},
      onChange,
      previousQuestion,
      showContent = false,
      isEditing = false,
      allowMultipleTypes = false,
    },
    ref
  ) => {
    const [formData, setFormData] = useState({
      question_number: 1,
      question: "",
      content: "",
      type: "multiple-choice",
      options: ["", "", "", ""],
      correctAnswer: "",
      blanks: [],
      explanation: "",
      tags: [],
      image: null,
      imagePreview: "",
      extraImage: null,
      extraImagePreview: "",
      audio: null,
      audioPreview: "",
      ...initialData,
    });

    const [usePreviousMedia, setUsePreviousMedia] = useState({
      image: false,
      audio: false,
      extraImage: false,
      content: false,
    });

    const [touched, setTouched] = useState({
      correctAnswer: false,
    });

    // Use ref to track if component is mounted to prevent calling onChange on initial mount
    const isMountedRef = useRef(false);
    const initialDataKeyRef = useRef(null);

    // Rich text with upload hooks - one for each editor
    // Content editor
    const contentRichText = useRichTextWithUpload();

    // Explanation editor
    const explanationRichText = useRichTextWithUpload();

    // Helper to check if any editor has images
    const hasAnyImages =
      contentRichText.hasImages || explanationRichText.hasImages;

    // Sync rich text values with formData
    useEffect(() => {
      if (contentRichText.description !== formData.content) {
        setFormData((prev) => ({
          ...prev,
          content: contentRichText.description,
        }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentRichText.description]);

    useEffect(() => {
      if (explanationRichText.description !== formData.explanation) {
        setFormData((prev) => ({
          ...prev,
          explanation: explanationRichText.description,
        }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [explanationRichText.description]);

    // Initialize rich text editors when initialData changes
    useEffect(() => {
      if (initialData?.content) {
        contentRichText.setRichTextValues(initialData.content);
      }
      if (initialData?.explanation) {
        explanationRichText.setRichTextValues(initialData.explanation);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData?._id]); // Only re-run when editing a different question

    // Expose method to parent component for processing images before submission
    useImperativeHandle(ref, () => ({
      /**
       * Process all images in rich text editors and return formData with final HTML
       * @returns {Promise<Object>} formData with processed HTML content
       */
      processImagesAndGetData: async () => {
        const [processedContent, processedExplanation] = await Promise.all([
          contentRichText.processAndUploadImages(),
          explanationRichText.processAndUploadImages(),
        ]);

        return {
          ...formData,
          content: processedContent,
          explanation: processedExplanation,
        };
      },
      /**
       * Check if any editor has images to upload
       * @returns {boolean}
       */
      hasImages: () => hasAnyImages,
      /**
       * Reset all rich text editors
       */
      resetRichText: () => {
        contentRichText.resetRichText();
        explanationRichText.resetRichText();
      },
    }));

    // Notify parent of changes (exclude onChange from deps to prevent infinite loops)
    useEffect(() => {
      if (isMountedRef.current && onChange) {
        onChange(formData);
      } else {
        isMountedRef.current = true;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData]);

    // Update form when initialData changes (only when genuinely new data arrives)
    useEffect(() => {
      if (initialData && Object.keys(initialData).length > 0) {
        // Create a stable key from the initialData to detect genuinely new data
        // vs. the same data bouncing back from parent via onChange
        const dataKey = JSON.stringify({
          question_number: initialData.question_number,
          question: initialData.question,
          type: initialData.type,
          partNumber: initialData.partNumber,
          _id: initialData._id,
        });

        if (dataKey !== initialDataKeyRef.current) {
          initialDataKeyRef.current = dataKey;
          setFormData((prev) => ({ ...prev, ...initialData }));
        }
      }
    }, [initialData]);

    const handleFormChange = (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleOptionChange = (index, value) => {
      const newOptions = [...formData.options];
      newOptions[index] = value;
      setFormData((prev) => ({
        ...prev,
        options: newOptions,
      }));
    };

    const handleAddOption = () => {
      const nextValue = String.fromCharCode(65 + formData.options.length); // E, F, etc.
      setFormData((prev) => ({
        ...prev,
        options: [...prev.options, nextValue],
      }));
    };

    const handleRemoveOption = (index) => {
      const newOptions = [...formData.options];
      const removedValue = newOptions[index];
      newOptions.splice(index, 1);

      const updates = { options: newOptions };

      // Clear correct answer if it was the removed option
      if (formData.correctAnswer === removedValue?.toLowerCase()) {
        updates.correctAnswer = "";
      }

      setFormData((prev) => ({ ...prev, ...updates }));
    };

    const handleFileChange = (obj) => {
      const { name, file } = obj || {};
      if (name) {
        if (file) {
          setFormData((prev) => ({
            ...prev,
            [name]: file,
          }));
          // Uncheck the corresponding checkbox when a new file is uploaded
          setUsePreviousMedia((prev) => ({ ...prev, [name]: false }));
        } else {
          setFormData((prev) => ({
            ...prev,
            [`${name}Url`]: "",
          }));
        }
      }
    };

    const handleUsePreviousMediaChange = (type) => (e) => {
      const isChecked = e.target.checked;
      setUsePreviousMedia((prev) => ({
        ...prev,
        [type]: isChecked,
      }));

      if (isChecked) {
        if (type === "content") {
          const previousContent = previousQuestion?.content || "";
          setFormData((prev) => ({
            ...prev,
            content: previousContent,
          }));
          // Update the rich text editor state
          contentRichText.setRichTextValues(previousContent);
          return;
        }
        if (previousQuestion) {
          let extraImage = {};
          if (type === "image") {
            extraImage = {
              extraImageFile: previousQuestion.extraImage || null,
              extraImageUrl: previousQuestion.extraImageUrl || "",
            };
          }
          setFormData((prev) => ({
            ...prev,
            [`${type}File`]: null,
            [`${type}Url`]: previousQuestion[`${type}Url`] || "",
            ...extraImage,
          }));
        }
      } else {
        if (type === "content") {
          setFormData((prev) => ({
            ...prev,
            content: "",
          }));
          // Clear the rich text editor
          contentRichText.setRichTextValues("");
        } else {
          setFormData((prev) => ({
            ...prev,
            [`${type}File`]: null,
            [`${type}Url`]: "",
          }));
        }
      }
    };

    return (
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Question #"
            type="number"
            value={formData.question_number}
            onChange={(e) =>
              handleFormChange("question_number", parseInt(e.target.value) || 1)
            }
            inputProps={{ min: 1 }}
            required
          />
        </Grid>

        {formData.type !== "fill-blank" && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Question Text"
              value={formData.question}
              onChange={(e) => handleFormChange("question", e.target.value)}
              multiline
              rows={3}
              helperText={formData.type === "mix-match" ? "This is the actual question (e.g., 'What task is the man in charge of?')" : ""}
            />
          </Grid>
        )}

        {allowMultipleTypes && (
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <Typography variant="subtitle2" gutterBottom>
                Question Type
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant={
                    formData.type === "multiple-choice"
                      ? "contained"
                      : "outlined"
                  }
                  onClick={() => handleFormChange("type", "multiple-choice")}
                >
                  Multiple Choice
                </Button>
                <Button
                  variant={
                    formData.type === "fill-blank" ? "contained" : "outlined"
                  }
                  onClick={() => handleFormChange("type", "fill-blank")}
                >
                  Fill in the Blank
                </Button>
                <Button
                  variant={
                    formData.type === "short_answer" ? "contained" : "outlined"
                  }
                  onClick={() => handleFormChange("type", "short_answer")}
                >
                  Short Answer
                </Button>
              </Box>
              {formData.type === "mix-match" && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  💡 Mix-Match combines fill-in-the-blank content with multiple-choice answers. Perfect for TOEIC Part 3/4 questions.
                </Typography>
              )}
            </FormControl>
          </Grid>
        )}

        {(formData.type === "multiple-choice" || formData.type === "mix-match") && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Answer Choices
              </Typography>
              <Grid container spacing={2}>
                {formData.options.map((option, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box flexGrow={1}>
                        <TextField
                          fullWidth
                          label={`Option ${String.fromCharCode(65 + index)}`}
                          value={option}
                          onChange={(e) =>
                            handleOptionChange(index, e.target.value)
                          }
                          size="small"
                        />
                      </Box>
                      <Box>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleRemoveOption(index)}
                          disabled={formData.options.length <= 2}
                        >
                          Remove
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                ))}
                {formData.options.length < 6 && (
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      onClick={handleAddOption}
                      startIcon={<AddIcon />}
                      size="small"
                    >
                      Add Option
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                error={
                  touched.correctAnswer &&
                  !formData.correctAnswer &&
                  formData.options.some((opt) => opt.trim() !== "")
                }
              >
                <InputLabel>Correct Answer</InputLabel>
                <Select
                  label="Correct Answer"
                  value={formData.correctAnswer.toUpperCase() || ""}
                  onChange={(e) => {
                    handleFormChange("correctAnswer", e.target.value);
                    setTouched((prev) => ({ ...prev, correctAnswer: true }));
                  }}
                  onBlur={() =>
                    setTouched((prev) => ({ ...prev, correctAnswer: true }))
                  }
                  required
                  renderValue={(selected) => {
                    if (!selected) return <em>Select correct answer</em>;

                    const index = selected.toLowerCase().charCodeAt(0) - 97;
                    const optionText = formData.options[index] || "";
                    return `${selected.toUpperCase()}. ${optionText}`;
                  }}
                >
                  {formData.options.map((option, index) => (
                    <MenuItem
                      key={index}
                      value={String.fromCharCode(65 + index)}
                    >
                      {String.fromCharCode(65 + index)}. {option}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Select which option is the correct answer
                </FormHelperText>
              </FormControl>
            </Grid>
          </>
        )}

        {formData.type === "short_answer" && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Correct Answer (Short Answer)"
              value={formData.correctAnswer || ""}
              onChange={(e) =>
                handleFormChange("correctAnswer", e.target.value)
              }
              multiline
              rows={2}
              placeholder="Enter the correct short answer..."
              helperText="Students will type their answer. Grading may require manual review."
              required
            />
          </Grid>
        )}

        {showContent && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Main Content{" "}
              {formData.type === "fill-blank" || formData.type === "mix-match"
                ? "(Required for Fill-in-the-Blank)"
                : "(Optional)"}
            </Typography>
            {formData.type !== "fill-blank" && (
              <Typography variant="caption" gutterBottom>
                If fill this, you can't add media
              </Typography>
            )}
            {(formData.type === "fill-blank" || formData.type === "mix-match") && allowMultipleTypes ? (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  gutterBottom
                  display="block"
                  mb={2}
                >
                  💡 Use <code>{"{{1}}"}</code>, <code>{"{{2}}"}</code>, etc. in
                  the content below to mark blank positions.
                  {formData.type === "mix-match" && " For mix-match, these blanks are for display only - students answer via multiple choice."}
                </Typography>
                <RichTextEditor
                  value={contentRichText.description}
                  onChange={contentRichText.handleDescriptionChange}
                  placeholder={formData.type === "mix-match" 
                    ? "Enter conversation/passage with blanks... Example: W: Theo, have you {{1}} the schedule for the employee {{2}} next month?"
                    : "Enter content with blanks here... Example: The weather is {{1}} today."}
                  readOnly={usePreviousMedia.content}
                  onContentImgUpload={contentRichText.handleContentImgChange}
                  onRichtextStateChange={
                    contentRichText.handleDescriptionStateChange
                  }
                />
              </Box>
            ) : (
              <RichTextEditor
                value={contentRichText.description}
                onChange={contentRichText.handleDescriptionChange}
                placeholder="Enter content here..."
                onContentImgUpload={contentRichText.handleContentImgChange}
                onRichtextStateChange={
                  contentRichText.handleDescriptionStateChange
                }
                readOnly={usePreviousMedia.content}
              />
            )}
            {previousQuestion && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={usePreviousMedia.content}
                    onChange={handleUsePreviousMediaChange("content")}
                    disabled={!previousQuestion?.content || previousQuestion.type === 'fill-blank'}
                  />
                }
                label="Use previous content"
                sx={{ mt: 1, ml: 1 }}
              />
            )}
          </Grid>
        )}

        {formData.type === "fill-blank" &&
          allowMultipleTypes &&
          showContent && (
            <Grid item xs={12}>
              <FillBlankInput
                blanks={formData.blanks || []}
                onChange={(blanks) => handleFormChange("blanks", blanks)}
                questionText={formData.content}
              />
            </Grid>
          )}
        
        {formData.type === "mix-match" &&
          allowMultipleTypes &&
          showContent && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  ℹ️ Mix-Match Question Setup
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  For mix-match questions, the blanks in the content above are for <strong>display purposes only</strong>. 
                  Students will answer the question below using the multiple-choice options you define.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Example:</strong> Content shows a conversation with blanks {'{{1}}'}, {'{{2}}'}, etc. 
                  The question asks "What task is the man in charge of?" with options A, B, C, D.
                </Typography>
              </Box>
            </Grid>
          )}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Question Image (Optional)
              </Typography>
              <FileUploadButton
                name="image"
                label="Upload Image"
                accept="image/*"
                onFileSelect={handleFileChange}
                file={formData.image}
                previewUrl={formData.imagePreview}
                disabled={!!usePreviousMedia.image || (!!formData.content && formData.type !== "fill-blank" && formData.type !== "mix-match")}
                onClear={() => handleFormChange("image", null)}
              />
              {previousQuestion && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={usePreviousMedia.image}
                      onChange={handleUsePreviousMediaChange("image")}
                      disabled={
                        !previousQuestion?.imageUrl ||
                        !!formData.image ||
                        (!!formData.content && formData.type !== "fill-blank" && formData.type !== "mix-match")
                      }
                    />
                  }
                  label="Use previous image"
                  sx={{ mt: 1, ml: 1 }}
                />
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Question Extra Image (Optional)
              </Typography>
              <FileUploadButton
                name="extraImage"
                label="Upload Image"
                accept="image/*"
                onFileSelect={handleFileChange}
                file={formData.extraImage}
                previewUrl={formData.extraImagePreview}
                disabled={!!usePreviousMedia.image || (!!formData.content && formData.type !== "fill-blank" && formData.type !== "mix-match")}
                onClear={() => handleFormChange("extraImage", null)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Question Audio (Optional)
              </Typography>
              <FileUploadButton
                name="audio"
                label="Upload Audio"
                accept="audio/*"
                onFileSelect={handleFileChange}
                file={formData.audio}
                previewUrl={formData.audioPreview}
                disabled={!!usePreviousMedia?.audio || (!!formData.content && formData.type !== "fill-blank" && formData.type !== "mix-match")}
                onClear={() => handleFormChange("audio", null)}
              />
              {previousQuestion && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={usePreviousMedia.audio}
                      onChange={handleUsePreviousMediaChange("audio")}
                      disabled={
                        !previousQuestion?.audioUrl ||
                        !!formData.audio ||
                        (!!formData.content && formData.type !== "fill-blank" && formData.type !== "mix-match")
                      }
                    />
                  }
                  label="Use previous audio"
                  sx={{ mt: 1, ml: 1 }}
                />
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel shrink sx={{ background: "white", fontSize: 20 }}>
              Explanation (Optional)
            </InputLabel>
            <Box
              sx={{
                mt: 2,
                border: "1px solid rgba(0, 0, 0, 0.23)",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <RichTextEditor
                value={explanationRichText.description}
                onChange={explanationRichText.handleDescriptionChange}
                onContentImgUpload={explanationRichText.handleContentImgChange}
                onRichtextStateChange={
                  explanationRichText.handleDescriptionStateChange
                }
              />
            </Box>
          </FormControl>
        </Grid>

      </Grid>
    );
  }
);

export default QuestionForm;
